import type { ChangeEvent } from "react";
import { useCallback, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { FxAdditionalInformationPanel } from "./mapping/FxAdditionalInformationPanel";
import { FxAwardsPanel } from "./mapping/FxAwardsPanel";
import { FxBannerPanel } from "./mapping/FxBannerPanel";
import { FxBenchmarkInformationPanel } from "./mapping/FxBenchmarkInformationPanel";
import { FxBenefitsPanel } from "./mapping/FxBenefitsPanel";
import { FxBrokerListPanel } from "./mapping/FxBrokerListPanel";
import { FxCollapsibleDisclaimerReactPanel } from "./mapping/FxCollapsibleDisclaimerReactPanel";
import { FxCommentaryPanel } from "./mapping/FxCommentaryPanel";
import { FxDefinitionListGroupPanel } from "./mapping/FxDefinitionListGroupPanel";
import { FxDefinitionListPanel } from "./mapping/FxDefinitionListPanel";
import { FxDisclosurePanel } from "./mapping/FxDisclosurePanel";
import { FxDocumentGroupPanel } from "./mapping/FxDocumentGroupPanel";
import { FxFeesAndExpensesRelatedPanel } from "./mapping/FxFeesAndExpensesRelatedPanel";
import { FxGipsAdditionalPanel } from "./mapping/FxGipsAdditionalPanel";
import { FxImportantInformationPanel } from "./mapping/FxImportantInformationPanel";
import { FxMedalistRatingPanel } from "./mapping/FxMedalistRatingPanel";
import { FxObjectivePanel } from "./mapping/FxObjectivePanel";
import { FxOffshoreRiskScoreRelativePanel } from "./mapping/FxOffshoreRiskScoreRelativePanel";
import { FxPerformanceGroupPanel } from "./mapping/FxPerformanceGroupPanel";
import { FxPerformancePanel } from "./mapping/FxPerformancePanel";
import { FxPricePanel } from "./mapping/FxPricePanel";
import { FxReadMoreReactPanel } from "./mapping/FxReadMoreReactPanel";
import { FxRiskConsiderationsPanel } from "./mapping/FxRiskConsiderationsPanel";
import { FxRiskScorePanel } from "./mapping/FxRiskScorePanel";
import { FxRiskScoreRelativePanel } from "./mapping/FxRiskScoreRelativePanel";
import { FxSourceRatingInformationPanel } from "./mapping/FxSourceRatingInformationPanel";
import { FxTextPanel } from "./mapping/FxTextPanel";
import { deviceType } from "react-device-detect";
import Accreditation from "@/components/Accreditation/Accreditation";
import { ContentMissing } from "@/components/ContentMissing/ContentMissing";
import { FlexColumn } from "@/components/FlexColumn/FlexColumn";
import { FlexRow } from "@/components/FlexRow/FlexRow";
import { FundDetailTopNavigation } from "@/components/FundDetailTopNavigation/FundDetailTopNavigation";
import { FundTermsAndConditionsModal } from "@/components/FundTermsAndConditionsModal/FundTermsAndConditionsModal";
import { InvestmentFigureUnitSelection } from "@/components/InvestmentFigureUnitSelection/InvestmentFigureUnitSelection";
import { LayoutHandler, type LayoutComponentConfig } from "@/components/LayoutHandler/LayoutHandler";
import { NavigatingLoadIndicator } from "@/components/NavigatingLoadIndicator/NavigatingLoadIndicator";
import { StickyBar } from "@/components/StickyBar/StickyBar";
import { FxAuthorisedParticipantsPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxAuthorisedParticipantsPanel";
import { FxBreakdownPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxBreakdownPanel";
import { FxListingsPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxListingsPanel";
import { FxManagerPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxManagerPanel";
import { FxPerformanceDiscretePanel } from "@/components/[Pages]/FundDetailPage/mapping/FxPerformanceDiscretePanel";
import { FxPerformanceTablePanel } from "@/components/[Pages]/FundDetailPage/mapping/FxPerformanceTablePanel";
import { FxProfileHeroPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxProfileHeroPanel";
import { FxSdrPanel } from "@/components/[Pages]/FundDetailPage/mapping/FxSdrPanel";
import { FxSustainabilityTablePanel } from "@/components/[Pages]/FundDetailPage/mapping/FxSustainabilityTablePanel";
import type { FundListItemPageRouteFunctionProps, FundListPageRouteFunctionProps } from "@/services/appRoutes";
import appRoutes from "@/services/appRoutes";
import type { ShareClassDashboard } from "@/services/server/v1/getShareClassDashboard";
import { useCanonicalCommit } from "@/services/useCanonicalCommit";
import { booleanTransformer, stringTransformer, useCanonicalState } from "@/services/useCanonicalState";
import type { FundDetailPagePathProps } from "@/types/FundDetailPagePathProps";
import type { FundDetailLayoutComponentMap } from "@/types/fundDetailLayout";
import { FxEtfHoldingsPanel } from "./mapping/FxEtfHoldingsPanel";

export interface FundDetailPageProps extends FundDetailPagePathProps {
	initialData: ShareClassDashboard;
}

export function FundDetailPage({ initialData: dashboard, location, language, channel, externalId }: FundDetailPageProps) {
	const { termsAndConditions, deviceSupport } = dashboard;
	const [debugHighlightMissingData] = useCanonicalState<boolean>("debugHighlightMissingData", false, booleanTransformer);

	const filteredComponentMap = Object.fromEntries(
		Object.entries(componentMap).map(({ 0: key, 1: value }) => {
			if (["fxTextPanel", "fxReadMoreReactPanel", "fxPerformanceDiscretePanel", "fxCollapsibleDisclaimerReactPanel"].includes(key)) {
				return [key, value];
			}

			const placeholderOrNull = debugHighlightMissingData
				? () => {
					return <ContentMissing component={key} />;
				}
				: null;

			return [key, key in dashboard ? value : placeholderOrNull];
		})
	);

	const [isUnitSelectionOpen, setIsUnitSelectionOpen] = useState<boolean>(false);

	// This sets up a state that reads its value from the URLs query string if possible, otherwise falling back to
	// the specified defaultValue. Other than that, it operates the same way the standard react useState() does.
	// When autoCommit is set to false, changes to the state using the setState function won't automatically get
	// reflected to the URL query params. The useCanonicalCommit() hook allows us to commit the changes to these
	// state values when we are ready (like when the user clicks a "Save" button) - Rob
	const baseCurrency = dashboard.shareClass.baseCurrency;
	const [unitType, setUnitType] = useCanonicalState<string>("unitType", "percentage", stringTransformer, {
		autoCommit: false
	});
	const [unitValue, setUnitValue] = useCanonicalState<string>("unitValue", "10000", stringTransformer, {
		autoCommit: false
	});
	const [unitCurrency, setUnitCurrency] = useCanonicalState<string>("unitCurrency", "USD", stringTransformer, {
		autoCommit: false
	});
	const numericUnitValue = parseFloat(unitValue);

	const onCanonicalCommit = useCanonicalCommit(["unitType", "unitValue", "unitCurrency"], { shallow: true });

	const onToggleOpen = useCallback(() => {
		setIsUnitSelectionOpen(!isUnitSelectionOpen);
	}, [isUnitSelectionOpen, setIsUnitSelectionOpen]);

	const onUnitTypeChange = useCallback(
		(value: string) => {
			setUnitType(value as "percentage" | "currency");
			setUnitCurrency(value === "currency" ? baseCurrency : "");
		},
		[setUnitType, setUnitCurrency, baseCurrency]
	);

	const onUnitValueChange = useCallback(
		(value: string) => {
			setUnitValue(value.slice(0, 10));
		},
		[setUnitValue]
	);

	const onSave = useCallback(() => {
		onToggleOpen();
		void onCanonicalCommit();
	}, [onToggleOpen, onCanonicalCommit]);

	const generateFundListUrlFromProps = useCallback(
		(props: FundListPageRouteFunctionProps) => {
			return appRoutes.fundListUrl("", { location, language, channel, ...props });
		},
		[location, channel, language]
	);
	const generateFundDetailUrlFromProps = useCallback(
		(props: FundListItemPageRouteFunctionProps) => {
			return appRoutes.fundListItemUrl({ location, language, channel, ...props });
		},
		[location, channel, language]
	);
	const scrollToComponent = useCallback((selector: string, disableSmoothScroll?: boolean) => {
		const targetElement = document.querySelector<HTMLElement>(selector);
		if (!targetElement) {
			return;
		}

		// Focus the element without scrolling
		if (targetElement instanceof HTMLElement && "focus" in targetElement) {
			targetElement.focus({ preventScroll: true });
		}

		const headerOffset = 144; // This value for the header height
		const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
		const offsetPosition = elementPosition - headerOffset;

		// check if user uses voice over assistance with the assumption
		// that user changed the setting on their device as well
		const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		window.scrollTo({
			top: offsetPosition,
			behavior: disableSmoothScroll || prefersReducedMotion ? "instant" : "smooth"
		});
	}, []);

	const validateComponent = useCallback(
		({ component, ...rest }: LayoutComponentConfig) => {
			const { type } = rest;

			if (component && deviceSupport[deviceType]?.[component] === false) {
				return false;
			}

			switch (component) {
				case "fxPerformanceDiscretePanel": {
					if (!type) {
						return !!dashboard["fxPerformanceDiscretePanel"];
					}
					return !!dashboard[`fxPerformanceDiscretePanel_${type}` as "fxPerformanceDiscretePanel"];
				}

				case "fxTextPanel": {
					const { textId } = rest;

					if (!type && !textId) {
						return !!dashboard["fxTextPanel"];
					}
					return !!dashboard[`fxTextPanel_${type || textId}` as "fxTextPanel"];
				}

				case "fxCollapsibleDisclaimerReactPanel": {
					const { disclaimer } = rest;

					return !!dashboard[`fxCollapsibleDisclaimerReactPanel_${disclaimer}` as "fxCollapsibleDisclaimerReactPanel"];
				}

				case "fxReadMoreReactPanel": {
					const { disclaimer } = rest;

					return !!dashboard[`fxReadMoreReactPanel_${disclaimer}` as "fxReadMoreReactPanel"];
				}
			}

			return true;
		},
		[dashboard, deviceSupport]
	);

	return (
		<>
			<NavigatingLoadIndicator />
			<FlexColumn flex={1} position="relative">
				{dashboard.fundDetailTopNavigation.isEnabled !== false && (
					<StickyBar>
						<Flex maxWidth="1100px" width="100%" height="inherit">
							<FundDetailTopNavigation href={generateFundListUrlFromProps({})} {...dashboard.fundDetailTopNavigation}>
								{dashboard.investmentFigureUnitSelection && (
									<FlexRow justifyContent="flex-end">
										<InvestmentFigureUnitSelection
											{...dashboard.investmentFigureUnitSelection}
											isOpen={isUnitSelectionOpen}
											onToggleOpen={onToggleOpen}
											onUnitTypeChange={onUnitTypeChange}
											onUnitValueChange={onUnitValueChange}
											onSave={onSave}
											unitType={unitType}
											unitValue={unitValue}
										/>
									</FlexRow>
								)}
							</FundDetailTopNavigation>
						</Flex>
					</StickyBar>
				)}
				<LayoutHandler
					componentMap={filteredComponentMap}
					{...dashboard.layoutHandler}
					componentProps={{
						dashboard,
						generateFundDetailUrlFromProps,
						scrollToComponent,
						unitType,
						unitValue: !isNaN(numericUnitValue) ? numericUnitValue : undefined,
						unitCurrency,
						location,
						channel,
						language,
						externalId
					}}
					validateComponent={validateComponent}
				>
					<Accreditation className="accreditationProfile" />
				</LayoutHandler>
			</FlexColumn>
			{termsAndConditions && <FundTermsAndConditionsModal data={termsAndConditions} />}
		</>
	);
}

export const componentMap: FundDetailLayoutComponentMap = {
	fxBreakdownPanel: FxBreakdownPanel,
	fxProfileHeroPanel: FxProfileHeroPanel,
	fxManagerPanel: FxManagerPanel,
	fxPerformanceTablePanel: FxPerformanceTablePanel,
	fxPerformancePanel: FxPerformancePanel,
	fxPerformanceDiscretePanel: FxPerformanceDiscretePanel,
	fxObjectivePanel: FxObjectivePanel,
	fxAdditionalInformationPanel: FxAdditionalInformationPanel,
	fxRiskConsiderationsPanel: FxRiskConsiderationsPanel,
	fxBenchmarkInformationPanel: FxBenchmarkInformationPanel,
	fxImportantInformationPanel: FxImportantInformationPanel,
	fxBenefitsPanel: FxBenefitsPanel,
	fxDisclosurePanel: FxDisclosurePanel,
	fxSourceRatingInformationPanel: FxSourceRatingInformationPanel,
	fxCommentaryPanel: FxCommentaryPanel,
	fxBrokerListPanel: FxBrokerListPanel,
	fxDefinitionListPanel: FxDefinitionListPanel,
	fxDefinitionListGroupPanel: FxDefinitionListGroupPanel,
	fxRiskScorePanel: FxRiskScorePanel,
	fxRiskScoreRelativePanel: FxRiskScoreRelativePanel,
	fxBannerPanel: FxBannerPanel,
	fxOffshoreRiskScoreRelativePanel: FxOffshoreRiskScoreRelativePanel,
	fxFeesAndExpensesRelatedPanel: FxFeesAndExpensesRelatedPanel,
	fxPricePanel: FxPricePanel,
	fxDocumentGroupPanel: FxDocumentGroupPanel,
	fxSustainabilityTablePanel: FxSustainabilityTablePanel,
	fxReadMoreReactPanel: FxReadMoreReactPanel,
	fxCollapsibleDisclaimerReactPanel: FxCollapsibleDisclaimerReactPanel,
	fxGipsAdditionalPanel: FxGipsAdditionalPanel,
	fxAwardsPanel: FxAwardsPanel,
	fxTextPanel: FxTextPanel,
	fxMedalistRatingPanel: FxMedalistRatingPanel,
	fxPerformanceGroupPanel: FxPerformanceGroupPanel,
	fxSdrPanel: FxSdrPanel,
	fxAuthorisedParticipantsPanel: FxAuthorisedParticipantsPanel,
	fxListingsPanel: FxListingsPanel,
	fxEtfHoldingsPanel: FxEtfHoldingsPanel
};
